import { Button, Input, Message, SelectPicker, useToaster } from 'rsuite';
import React, { Dispatch, SetStateAction, useState } from 'react';

import * as studentsApi from '../../api/students';
import { emptyf } from '../../common/common';
import DynamicTextArea from '../../common/DynamicTextArea';
import {
  FormField,
  FormGrid,
  FormModal,
  FormSection,
} from '../../common/FormModal';
import { useFormErrors } from '../../common/useFormErrors';
import StudentQueuesModal from './StudentQueuesModal';

type Props = {
  obj: any | null;
  setObj: Dispatch<SetStateAction<any | null>>;
  typesData: { value: number; label: string }[];
  coursesData: { value: string; label: string }[];
  callback?: () => void;
};

export default function EditStudentModal({
  obj,
  setObj,
  typesData,
  coursesData,
  callback = emptyf,
}: Props) {
  const toaster = useToaster();
  const { errorSummary, setApiError, clearErrors } = useFormErrors({
    fallbackMessage: 'Не вдалося оновити дані студента. Спробуйте пізніше',
    resetOn: [obj?.id],
  });

  const [studentId, setStudentId] = useState('');
  return (
    <FormModal
      open={!!obj}
      onClose={() => setObj(null)}
      title="Редагувати студента"
      errorSummary={errorSummary}
      footer={
        <>
          <Button
            onClick={async () => {
              clearErrors();
              try {
                await studentsApi.update(obj.id, obj);
                toaster.push(
                  <Message type="success" showIcon closable>
                    Дані студента оновлено
                  </Message>,
                  {
                    placement: 'topEnd',
                    duration: 1500,
                  }
                );
                setObj(null);
                callback();
              } catch (e: any) {
                console.error(e);
                const errorMessage = setApiError(e)[0];
                toaster.push(
                  <Message type="error" showIcon closable>
                    {errorMessage}
                  </Message>,
                  {
                    placement: 'topEnd',
                    duration: 1500,
                  }
                );
              }
            }}
            appearance="primary"
          >
            Зберегти
          </Button>
          <Button onClick={() => setObj(null)} appearance="subtle">
            Скасувати
          </Button>
        </>
      }
    >
      <FormSection title="Групи студента">
        {obj?.groups && obj?.groups.length > 0 && (
          <div>
            <h6>Групи студента:</h6>
            <ul>
              {obj?.groups
                .sort((a: any, b: any) => b.createdAt - a.createdAt)
                .map((g: any) => (
                  <li key={g.id}>
                    <b>{g.name}</b> - навчання{' '}
                    {g.finishedAt
                      ? `завершено ${new Date(g.finishedAt).toLocaleString()}`
                      : 'не завершено'}
                  </li>
                ))}
            </ul>
          </div>
        )}
        <Button
          appearance="default"
          onClick={() => {
            setStudentId(obj.id);
          }}
        >
          Черги курсів&nbsp;&nbsp;&nbsp;
          <img width={16} src="/add_to_list.svg" />
        </Button>
      </FormSection>

      <FormSection title="Дані студента">
        <FormGrid>
          <FormField label="Пошта" id="edit-student-email">
            <Input
              placeholder="Пошта"
              value={obj?.email || ''}
              onChange={email =>
                setObj((prev: any | null) =>
                  !!prev ? { ...prev, email: email || null } : null
                )
              }
            />
          </FormField>
          <FormField label="Тип" id="edit-student-type">
            <SelectPicker
              data={typesData}
              placeholder="Тип"
              searchable={false}
              value={obj?.type}
              onChange={value => {
                if (value === null) {
                  return;
                }
                setObj((prev: any | null) =>
                  !!prev ? { ...prev, type: value } : null
                );
              }}
            />
          </FormField>
        </FormGrid>
        <FormGrid>
          <FormField label="Ім'я" id="edit-student-first-name">
            <Input
              placeholder="Ім'я"
              value={obj?.firstName || ''}
              onChange={firstName =>
                setObj((prev: any | null) =>
                  !!prev
                    ? {
                        ...prev,
                        firstName: firstName || null,
                      }
                    : null
                )
              }
            />
          </FormField>
          <FormField label="Прізвище" id="edit-student-second-name">
            <Input
              placeholder="Прізвище"
              value={obj?.secondName || ''}
              onChange={secondName =>
                setObj((prev: any | null) =>
                  !!prev
                    ? {
                        ...prev,
                        secondName: secondName || null,
                      }
                    : null
                )
              }
            />
          </FormField>
        </FormGrid>
        <FormGrid>
          <FormField label="Телефон" id="edit-student-phone">
            <Input
              placeholder="Телефон"
              value={obj?.phone || ''}
              onChange={phone =>
                setObj((prev: any | null) =>
                  !!prev ? { ...prev, phone: phone || null } : null
                )
              }
            />
          </FormField>
          <FormField label="Коментар" id="edit-student-comment">
            <DynamicTextArea
              placeholder="Коментар"
              value={obj?.data || ''}
              onChange={data =>
                setObj((prev: any | null) =>
                  !!prev
                    ? {
                        ...prev,
                        data: data || null,
                      }
                    : null
                )
              }
            />
          </FormField>
        </FormGrid>
      </FormSection>
      {studentId.length > 0 && (
        <StudentQueuesModal
          studentId={studentId}
          setStudentId={setStudentId}
          coursesData={coursesData}
        />
      )}
    </FormModal>
  );
}
